import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import IconButton from '@mui/material/IconButton';
import MuiAlert from '@mui/material/Alert';
import isCyrillic from 'helpers/isCyrillic';
import { styled } from '@mui/system';
import renderHtml from 'helpers/renderHtml';
import CloseIcon from 'assets/icons/daily/fluent-emoji-high-contrast_cross-mark.svg?react';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AlertStyled = styled(Alert)(({ severity, theme }) => ({
  borderRadius: '15px',
  position: 'relative',
  marginBottom: 22,
  boxShadow: 'none',
  opacity: 1, // Ensure full opacity
  '& .MuiAlert-message': {
    fontWeight: 400, // Regular instead of medium
    '& strong, & b': {
      fontWeight: 500 // Bold becomes medium
    }
  },
  ...(severity === 'dailyQuest'
    ? {
        backgroundColor: '#FFFEF0', // Same yellow as cookies banner
        '& .MuiAlert-message': {
          color: theme.palette.textPrimary?.main || '#000000', // High contrast text color
          opacity: 1, // Ensure text is fully opaque
          fontWeight: 400, // Regular instead of medium
          '& strong, & b': {
            fontWeight: 500 // Bold becomes medium
          }
        }
      }
    : {}),
  [theme.breakpoints.down('sm')]: {
    marginBottom: 16,
    borderRadius: '12px',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    // Explicitly set background color on mobile to ensure visibility
    backgroundColor: severity === 'dailyQuest' 
      ? '#FFFEF0' // Same yellow as cookies banner 
      : severity === 'error' 
      ? theme.palette.error.main 
      : severity === 'warning'
      ? theme.palette.warning?.main || '#ed6c02'
      : severity === 'info'
      ? theme.palette.info?.main || '#0288d1'
      : theme.palette.success?.main || '#2e7d32',
    '& .MuiAlert-message': {
      color: severity === 'dailyQuest' 
        ? (theme.palette.textPrimary?.main || '#000000') // High contrast for dailyQuest on mobile too
        : '#fff',
      opacity: 1, // Ensure text is fully opaque
      fontWeight: severity === 'dailyQuest' ? 400 : 'inherit', // Regular instead of medium for dailyQuest
      width: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
      wordWrap: 'break-word',
      '& strong, & b': {
        fontWeight: 500 // Bold becomes medium
      }
    }
  },
  '& .MuiAlert-action': {
    position: 'absolute',
    right: -12,
    top: -12,
    left: 'auto',
    padding: 0,
    margin: 0,
    borderRadius: '50%',
    backgroundColor: theme.palette.grey[100],
    '& button': {
      padding: 5,
      margin: 0
    },
    [theme.breakpoints.down('sm')]: {
      right: 8,
      top: 8,
      left: 'auto'
    }
  }
}));

const ContentWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    paddingRight: 40,
    minWidth: 0,
    '& > span': {
      minWidth: 0,
      overflow: 'hidden',
      wordWrap: 'break-word'
    }
  }
}));

const ErrorSnackbar = ({
  closeDelay = 5000,
  error: { message, variant, icon, details, autoClose = true },
  onClose
}) => {
  const t = useTranslate(variant === 'dailyQuest' ? 'DAILY' : 'ERRORS');
  const errors = useSelector((state) => state?.error?.list || []);

  const handleClose = React.useCallback((e) => onClose(e), [onClose]);

  React.useEffect(() => {
    if (!autoClose) return;

    setTimeout(() => {
      if (!errors.length) return;

      if (!['error', 'dailyQuest'].includes(variant)) {
        onClose();
      }
    }, closeDelay);
  }, [autoClose, errors, closeDelay, onClose, variant]);

  const translateDetails = React.useCallback(
    (details) => {
      if (!details) return {};

      return Object.keys(details).reduce((acc, key) => {
        const value = details[key];
        // Don't translate numbers - they are interpolation values
        if (typeof value === 'number' || typeof value === 'boolean') {
          acc[key] = value;
          return acc;
        }
        if (isCyrillic(value) && typeof value === 'string') {
          acc[key] = value;
          return acc;
        }
        acc[key] = t(value);
        return acc;
      }, {});
    },
    [t]
  );

  return (
    <AlertStyled
      icon={false}
      severity={variant || 'error'}
      variant="filled"
      action={
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      }>
      <ContentWrapper>
        {icon ? <span>{icon}</span> : null}
        <span>
          {renderHtml(isCyrillic(message) ? message : t(message, translateDetails(details)))}
        </span>
      </ContentWrapper>
    </AlertStyled>
  );
};

export default ErrorSnackbar;
